import { useEffect, type PropsWithChildren, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../cn";
import { useT } from "../../i18n";

/** Cosmic modal dialog (portal + backdrop + ESC). */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  testId,
  size = "md",
}: PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  footer?: ReactNode;
  testId?: string;
  size?: "md" | "lg";
}>) {
  const t = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
      <div
        data-testid={testId ? `${testId}-backdrop` : undefined}
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-black/75 backdrop-blur-md animate-[mktFade_.2s_ease-out]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        className={cn(
          "relative neon-glass-strong rounded-xl border border-border-strong shadow-elevated",
          "w-full flex flex-col max-h-[88vh] animate-[mktRise_.24s_cubic-bezier(0.16,1,0.3,1)]",
          size === "lg" ? "max-w-2xl" : "max-w-md",
        )}
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-border-divider shrink-0">
          <div>
            <h3 className="font-display text-base font-semibold tracking-[0.04em] text-text-primary uppercase">{title}</h3>
            {subtitle && <p className="mt-1 font-mono text-[10.5px] tracking-[0.12em] text-text-muted uppercase">{subtitle}</p>}
          </div>
          <button
            type="button"
            data-testid={testId ? `${testId}-close` : undefined}
            aria-label={t("marketplace.common.close")}
            onClick={onClose}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-text-secondary hover:text-neon-teal hover:bg-elevated shrink-0"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
        {footer && <footer className="px-6 py-4 border-t border-border-divider shrink-0">{footer}</footer>}
      </div>
      <style>{`@keyframes mktFade{from{opacity:0}to{opacity:1}}@keyframes mktRise{from{transform:translateY(14px) scale(.98);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}`}</style>
    </div>,
    document.body,
  );
}
